function handleOrientation(value) {
  switch (value) {
    case 0:
      return 'Portrait';
    case 1:
      return 'Landscape';
    default:
      return 'Reverse Landscape';
  }
}

function handleTheme(value) {
  switch (value) {
    case 1:
      return 'Dark';
    case 2:
      return 'Light';
    case 3:
      return 'Red';
    case 4:
      return 'Green';
    default:
      return 'Pink';
  }
}

function handleLCUHeadingAngleUnit(value) {
  if (value === 0) return '°';
  else if (value > 0) return '° to port';
  else return '° to starboard';
}

function handleRudderSourceVal(value) {
  switch (value) {
    case 1:
      return '4-20mA'; // not used in lightning
    case 2:
      return '0-5V'; // not used in lightning
    case 3:
      return 'NMEA0183 RSA'; // not used in lightning
    case 4:
      return 'PENTA'; // not used in lightning
    case 5:
      return 'NMEA2000';
    case 6:
      return 'NMEA0183 STC';
    case 7:
      return 'Virtual Rudder';
    default:
      return 'None';
  }
}

function handleTrimConcept(value) {
  switch (value) {
    case 1:
      return 'Curve editor';
    default:
      return 'Simplified';
  }
}

function parseCommaSeparatedString(str) {
  if (!str) return [];
  return str.split(',').map((val) => {
    const num = parseInt(val.trim(), 10);
    return isNaN(num) ? 0 : num;
  });
}

const defaultCurveArray = Array.from({ length: 41 }, () => 0);

function parseCommaSeparatedStringASCIIToString(str) {
  if (!str) return '';
  const charCodes = str.split(',').map((val) => {
    const num = parseInt(val.trim(), 10);
    return isNaN(num) ? 0 : num;
  });
  return String.fromCharCode(...charCodes);
}

// Helper to extract correct metadata depending on mode
function getMeta(systemMetaData) {
  if (systemMetaData?.lightningHardwareSystem) {
    return {
      lcu: systemMetaData.lightningHardwareSystem.controlUnit?.metadata || {},
      lcp: systemMetaData.lightningHardwareSystem.primaryControlPanel?.metadata || {},
    };
  }
  return {
    lcu: systemMetaData?.lcuMetadata || {},
    lcp: systemMetaData?.lcpMetadata || {},
  };
}

const sensorData = (systemMetaData) => {
  const { lcu } = getMeta(systemMetaData);
  return [
    {
      id: 'axisConfig',
      name: 'LCU orientation',
      value: lcu.axisConfig,
      unit: '',
      hasTooltip: true,
      defaultValue: 0,
    },
    {
      id: 'srt_TurnAng',
      name: 'LCU heading angle',
      value: lcu.srt_TurnAng,
      unit: handleLCUHeadingAngleUnit(lcu.srt_TurnAng),
      hasTooltip: false,
      defaultValue: 0,
    },
  ];
};

const rudderData = (systemMetaData) => {
  const { lcu } = getMeta(systemMetaData);
  return [
    {
      id: 're_SourceVal',
      name: 'Rudder source',
      value: lcu.re_SourceVal,
      displayValue: handleRudderSourceVal(lcu.re_SourceVal),
      unit: '',
      defaultValue: 7,
    },
    {
      id: 'vr_YawRateMaxStateNeg',
      name: 'Yaw rate negative',
      value: lcu.vr_YawRateMaxStateNeg,
      displayValue: lcu.vr_YawRateMaxStateNeg?.toString() || '0',
      unit: '',
      defaultValue: 0,
    },
    {
      id: 'vr_YawRateMaxStatePos',
      name: 'Yaw rate positive',
      value: lcu.vr_YawRateMaxStatePos,
      displayValue: lcu.vr_YawRateMaxStatePos?.toString() || '0',
      unit: '',
      defaultValue: 0,
    },
    {
      id: 're_PortVal',
      name: 'Port value',
      value: lcu.re_PortVal,
      unit: '',
      defaultValue: 0,
    },
    {
      id: 're_CenterVal',
      name: 'Center value',
      value: lcu.re_CenterVal,
      unit: '',
      defaultValue: 0,
    },
    {
      id: 're_StbdVal',
      name: 'Starboard value',
      value: lcu.re_StbdVal,
      unit: '',
      defaultValue: 0,
    },
  ];
};

const interceptorSetupData = (systemMetaData) => {
  const { lcu } = getMeta(systemMetaData);
  return [
    {
      id: 'servo0_Offset',
      name: 'Port offset',
      value: lcu.servo0_Offset,
      unit: '%',
      defaultValue: 0,
    },
    {
      id: 'servo1_Offset',
      name: 'Starboard offset',
      value: lcu.servo1_Offset,
      unit: '%',
      defaultValue: 0,
    },
  ];
};

const speedSourceData = (systemMetaData) => {
  const { lcp } = getMeta(systemMetaData);
  return [
    {
      id: 'prioritizeWaterSpeed',
      name: 'Prioritize waterspeed',
      value: lcp.prioritizeWaterSpeed,
      unit: '',
      defaultValue: false,
    },
    {
      id: 'allowNoFixGPS',
      name: 'Allow nofix',
      value: lcp.allowNoFixGPS,
      unit: '',
      defaultValue: false,
    },
  ];
};

const autoTrimData = (systemMetaData) => {
  const { lcu } = getMeta(systemMetaData);
  return [
    {
      id: 'at_HumpSpeed',
      name: 'Hump speed',
      value: lcu.at_HumpSpeed,
      unit: 'kn',
      defaultValue: 12,
    },
    {
      id: 'at_Amplitude',
      name: 'Hump speed extension',
      value: lcu.at_Amplitude,
      unit: '%',
      defaultValue: 0,
    },
    {
      id: 'at_EndSpeed',
      name: 'Trim max speed',
      value: lcu.at_EndSpeed,
      unit: 'kn',
      defaultValue: 25,
    },
    {
      id: 'at_EndAmplitude',
      name: 'Max speed extension',
      value: lcu.at_EndAmplitude,
      unit: '%',
      defaultValue: 0,
    },
    {
      id: 'man_TrimMemoryEnabled',
      name: 'Manual trim memory enabled',
      value: lcu.man_TrimMemoryEnabled,
      unit: '',
      defaultValue: false,
    },
    {
      id: 'at_CalibConcept',
      name: 'Trim concept',
      value: lcu.at_CalibConcept,
      displayValue: handleTrimConcept(lcu.at_CalibConcept),
      unit: '',
      defaultValue: 0,
    },
    {
      id: 'at_Curve1',
      name: 'Trim curve 1',
      value: parseCommaSeparatedString(lcu.at_Curve1),
      unit: parseCommaSeparatedStringASCIIToString(lcu.at_CurveName1),
      defaultValue: defaultCurveArray,
    },
    {
      id: 'at_Curve2',
      name: 'Trim curve 2',
      value: parseCommaSeparatedString(lcu.at_Curve2),
      unit: parseCommaSeparatedStringASCIIToString(lcu.at_CurveName2),
      defaultValue: defaultCurveArray,
    },
    {
      id: 'at_Curve3',
      name: 'Trim curve 3',
      value: parseCommaSeparatedString(lcu.at_Curve3),
      unit: parseCommaSeparatedStringASCIIToString(lcu.at_CurveName3),
      defaultValue: defaultCurveArray,
    },
    {
      id: 'at_Curve4',
      name: 'Trim curve 4',
      value: parseCommaSeparatedString(lcu.at_Curve4),
      unit: parseCommaSeparatedStringASCIIToString(lcu.at_CurveName4),
      defaultValue: defaultCurveArray,
    },
  ];
};

const listRollData = (systemMetaData) => {
  const { lcu } = getMeta(systemMetaData);
  return [
    {
      id: 'alSensitivity',
      name: 'List sensitivity',
      value: lcu.alSensitivity,
      unit: '',
      defaultValue: 0,
    },
    {
      id: 'OnSpeed',
      name: 'Activation speed',
      value: lcu.onSpeed,
      unit: 'kn',
      defaultValue: 9,
    },
    {
      id: 'OffSpeed',
      name: 'Deactivation speed',
      value: lcu.offSpeed,
      unit: 'kn',
      defaultValue: 7.5,
    },
    {
      id: 'td_TurnDetectActivationLimit',
      name: 'Turn detect limit',
      value: lcu.td_TurnDetectActivationLimit,
      unit: '°/s',
      defaultValue: 3.5,
    },
    {
      id: 'td_TurnDetectDeactivationLimit',
      name: 'Turn exit limit',
      value: lcu.td_TurnDetectDeactivationLimit,
      unit: '°/s',
      defaultValue: 2.5,
    },
    {
      id: 'al_TrueTurnOffset',
      name: 'True turn compensation',
      value: lcu.al_TrueTurnOffset,
      unit: '',
      defaultValue: 1,
    },
    {
      id: 'ar_Sensitivity',
      name: 'Roll sensitivity',
      value: lcu.ar_Sensitivity,
      unit: '',
      defaultValue: 0,
    },
  ];
};

const coTurnData = (systemMetaData) => {
  const { lcu } = getMeta(systemMetaData);
  return [
    {
      id: 'ct_RudderDeadBand',
      name: 'Rudder deadband',
      value: lcu.ct_RudderDeadBand,
      unit: '%',
      defaultValue: 0,
    },
    {
      id: 'ct_LowSpeedGain',
      name: 'Low speed gain',
      value: lcu.ct_LowSpeedGain,
      unit: '%',
      defaultValue: 100,
    },
    {
      id: 'ct_TransitionSpeed',
      name: 'Transition speed',
      value: lcu.ct_TransitionSpeed,
      unit: 'kn',
      defaultValue: 25,
    },
    {
      id: 'ct_HighSpeedGain',
      name: 'High speed gain',
      value: lcu.ct_HighSpeedGain,
      unit: '%',
      defaultValue: 100,
    },
    {
      id: 'ct_CalibConcept',
      name: 'Turn concept',
      value: lcu.ct_CalibConcept,
      displayValue: handleTrimConcept(lcu.ct_CalibConcept),
      unit: '',
      defaultValue: 0,
    },
    {
      id: 'ct_CurveData',
      name: 'Curve',
      value: parseCommaSeparatedString(lcu.ct_CurveData),
      unit: '',
      defaultValue: defaultCurveArray,
    },
  ];
};

const pitchData = (systemMetaData) => {
  const { lcu } = getMeta(systemMetaData);
  return [
    {
      id: 'ap_Sensitivity',
      name: 'Pitch sensitivity',
      value: lcu.ap_Sensitivity,
      unit: '',
      defaultValue: 0,
    },
    {
      id: 'ap_PitchAccMaxBowDownStroke',
      name: 'Max bow down stroke',
      value: lcu.ap_PitchAccMaxBowDownStroke,
      unit: '%',
      defaultValue: 20,
    },
    {
      id: 'ap_PitchAccBowDownThreshold',
      name: 'Bow down threshold',
      value: lcu.ap_PitchAccBowDownThreshold,
      unit: '',
      defaultValue: 1,
    },
  ];
};

const displayData = (systemMetaData) => {
  const { lcp } = getMeta(systemMetaData);
  return [
    {
      id: 'theme',
      name: 'Theme',
      value: lcp.theme,
      displayValue: handleTheme(lcp.theme),
      unit: '',
      defaultValue: 2,
    },
    {
      id: 'orientation',
      name: 'Orientation',
      value: lcp.orientation,
      displayValue: handleOrientation(lcp.orientation),
      unit: '',
      defaultValue: 0,
    },
  ];
};

export {
  sensorData,
  rudderData,
  displayData,
  interceptorSetupData,
  speedSourceData,
  autoTrimData,
  pitchData,
  coTurnData,
  listRollData,
};
